import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { SITE } from "../data/siteData";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55 },
};

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const payload = JSON.stringify(form);
    const post = async (url) => {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
      });
      const data = await res.json();
      if (!res.ok || data.status !== "success") throw new Error("send failed");
    };
    try {
      // On static hosting (Hostinger) the PHP mail script handles the enquiry.
      // In the preview environment the Python service is the fallback.
      try {
        await post("contact.php");
      } catch {
        await post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`);
      }
      toast.success("Thank you for reaching out!", {
        description: "Your enquiry has been sent to our team. We typically respond within one working day.",
      });
      setForm({ name: "", email: "", phone: "", subject: "General Enquiry", message: "" });
    } catch {
      toast.error("Could not send your message", {
        description: "Please try again, or email us directly at info@cytocare.in.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        testId="contact-header"
        eyebrow="Contact Us"
        title="Let's talk about better care"
        subtitle="Whether you are a doctor, hospital, distributor or a future colleague — drop us a line and the Cytocare team will get back to you."
      />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-5">
          {/* Email card */}
          <motion.div {...fadeUp} className="lg:col-span-2" data-testid="contact-info">
            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#1D4ED8]">
                <Mail size={22} />
              </div>
              <h3 className="mt-5 text-lg font-bold text-slate-900">Email Us</h3>
              <a
                data-testid="contact-email"
                href={`mailto:${SITE.email}`}
                className="mt-2 block text-sm font-semibold text-[#1D4ED8] transition-colors hover:text-[#173CAE]"
              >
                {SITE.email}
              </a>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                Write to us about product enquiries, hospital or pharmacy procurement, distribution
                partnerships or careers — we typically respond within one working day.
              </p>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div {...fadeUp} className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10 lg:col-span-3" data-testid="contact-form-card">
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Send us a message</h2>
            <p className="mt-2 text-sm text-slate-500">Your message goes straight to our inbox at {SITE.email}.</p>
            <form onSubmit={handleSubmit} className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="contact-form">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700" htmlFor="name">Full Name *</label>
                <input id="name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" data-testid="contact-name-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700" htmlFor="email">Email *</label>
                <input id="email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="contact-email-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700" htmlFor="phone">Phone</label>
                <input id="phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Your contact number" data-testid="contact-phone-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-700" htmlFor="subject">Subject</label>
                <select id="subject" value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject-select">
                  <option>General Enquiry</option>
                  <option>Hospital / Pharmacy Procurement</option>
                  <option>Distribution Partnership</option>
                  <option>Medical Representative</option>
                  <option>Careers</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-slate-700" htmlFor="message">Message *</label>
                <textarea id="message" required rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="How can we help?" data-testid="contact-message-input" />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-8 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#173CAE] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send size={15} /> {sending ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
